import React, { useState } from "react"
import { Icon, DataTable, Chip, Button } from '@arnsbogroup/react-voicearchive'
import { Span, Buyouts, FilePreview } from 'components/datatable'
import messages from "./messages"
import { FormattedMessage, useIntl } from 'react-intl'
import SubmittedTableCommentModel from './SubmittedTableCommentModel'
import SubmittedTableRejectModel from './SubmittedTableRejectModel'
import useCurrentUser from 'hooks/useCurrentUser'
import NiceModal from '@ebay/nice-modal-react'

const SubmittedTable = ({ entries = [], onDuplicate, refetch, orderId }) => {
  const { formatMessage } = useIntl()
  const { roles } = useCurrentUser()
  const [selectedTaskIds, setSelectedTaskIds] = useState([])

  const getLSDateState = (accepted, rejected) => {
    let state = 'warning'
    if (+ accepted) {
      state = 'success'
    } else if (+ rejected) {
      state = 'error'
    } else {
      state = 'warning'
    }

    return state
  }

  const LSDates = ({lsDates = []}) => {
    let formattedDates = lsDates.map(({ dateStart, dateEnd, accepted, rejected }) => {
      if (!dateStart || !dateEnd) {
        return null
      }
      const startDate = new Date(parseInt(dateStart) * 1000)
      const endDate = new Date(parseInt(dateEnd) * 1000)
      const startFormatted = `${(startDate.getMonth() + 1).toString().padStart(2, '0')}/${startDate.getDate().toString().padStart(2, '0')} ${startDate.getHours().toString().padStart(2, '0')}:${startDate.getMinutes().toString().padStart(2, '0')}`
      const endFormatted = `${endDate.getHours().toString().padStart(2, '0')}:${endDate.getMinutes().toString().padStart(2, '0')}`
      const formattedString = `${startFormatted} - ${endFormatted}`

      return {
        date: formattedString,
        state: getLSDateState(accepted, rejected)
      }
    }).filter((entry) => entry)

    return <div className="space-y-1">
      { formattedDates.map(({ date, state }) => (
        <div>
          <Chip 
            state={state}
            title={
              {
                success:   'Accepted',
                error:   'Rejected',
                warning: 'Pending'
              }[state] ?? 'Pending'
            }
          >
            { date }
          </Chip>
        </div>
      ))}
    </div>
  }

  const head = () => {
    if (roles.includes('coordinator') || roles.includes('sales')) {
      return {
        supplier: { label: formatMessage(messages.tableHeadTalent) },
        buyouts: { label: formatMessage(messages.tableHeadBuyouts), className: 'w-1/6' },
        cost: { label: formatMessage(messages.tableHeadCost) },
        price: { label: formatMessage(messages.tableHeadPrice) },
        lsCost: { label: formatMessage(messages.tableHeadLiveSessionCost) },
        lsDates: { label: formatMessage(messages.tableHeadLiveSessionDates) },
        supplier_note_internal: { label: formatMessage(messages.tableHeadComment) },
        service: { label: formatMessage(messages.tableHeadService) },
        status: { label: formatMessage(messages.tableHeadStatus) },
        files: { label: formatMessage(messages.preview), className: 'w-40'},
      }
    } else {
      return {
        supplier: { label: formatMessage(messages.tableHeadTalent) },
        buyouts: { label: formatMessage(messages.tableHeadBuyouts), className: 'w-1/6' },
        price: { label: formatMessage(messages.tableHeadPrice) },
        lsCost: { label: formatMessage(messages.tableHeadLiveSessionCost) },
        lsDates: { label: formatMessage(messages.tableHeadLiveSessionDates) },
        supplier_note_external: { label: formatMessage(messages.tableHeadComment) },
        service: { label: formatMessage(messages.tableHeadService) },
        status: { label: formatMessage(messages.tableHeadStatus) },
        files: { label: formatMessage(messages.preview), className: 'w-40'},
        action: {label: <Icon icon={['fas', 'ellipsis-v']} />, className: 'w-16'}
      }
    }
  }

  const body = () => {
    if (roles.includes('coordinator') || roles.includes('sales')) {
      return {
        supplier: (entry) =>
          <div title={!!(+entry.rejected) ? "Rejected" : entry.supplier.userId}>
            <Span
              className={!!(+entry.rejected) ? "text-negative" : ""}
            >
              {entry.priorities && entry.priorities.split(',').includes('voicestar') &&
                <Icon icon={['fas', 'star']} className="text-primary fa-fw" />
              }
              {entry.supplier.userId ? entry.supplier.userId : ''}
            </Span>
          </div>,
        buyouts: (entry) => 
          <Buyouts 
            buyouts={formatBuyouts(entry.buyouts)}
            supplierApproval={entry.supplierapproval}
            declinedReason={entry.declinedreason}
          />,
        cost: (entry) => <Span>{calculateAmount(entry.buyouts, 'cost')}</Span>,
        price: (entry) => <Span>{calculateAmount(entry.buyouts, 'price')}</Span>,
        lsCost: (entry) => <Span>{entry.lsTask?.lsCost || ""} {entry.lsTask?.lsCostCurrency?.iso || ""}</Span>,
        lsDates: (entry) => <LSDates lsDates={entry.lsTask?.lsDates || []} />,
        supplier_note_internal: (entry) =>  
          <SupplierNoteIcon
            note={entry.supplier_note_internal}
            visible={entry.supplier_note_internal}
            onClick={() => {showCommentModal(entry.taskId, entry.supplier_note_internal, entry.supplier_note_external)}}
          />,
        service: Span,
        status: Span,
        files: (entry) => formatFilePreview(entry),
      }
    } else {
      return {
        supplier: (entry) => <Span>
            {entry.priorities && entry.priorities.split(',').includes('voicestar') &&
              <Icon icon={['fas', 'star']} className="text-primary fa-fw" />
            }
            {entry.supplier.userId ? entry.supplier.userId : ''}
          </Span>,
        buyouts: (entry) => 
          <Buyouts 
            buyouts={formatBuyouts(entry.buyouts)}
            supplierApproval={entry.supplierapproval}
            declinedReason={entry.declinedreason}
          />,
        price: (entry) => <Span>{calculateAmount(entry.buyouts, 'price')}</Span>,
        lsDates: (entry) => <LSDates lsDates={entry.lsTask?.lsDates || []} />,
        supplier_note_external: (entry) =>  
          <SupplierNoteIcon
            note={entry.supplier_note_external}
            visible={entry.supplier_note_external}
            onClick={() => {showCommentModal(entry.taskId, entry.supplier_note_internal, entry.supplier_note_external)}}
          />,
        status: Span,
        files: (entry) => formatFilePreview(entry),
        action: (entry) => 
          <span className="cursor-pointer" title={formatMessage(messages.downloadFile)} onClick={() => onFileDownload(entry)}>
            <Icon className={['READY', 'CLOSED'].includes(entry.status) && entry.filesDownloadLink ? "text-primary" : "text-gray-300"} icon={['fas', 'download']} />
          </span>,
      }
    }
  }

  const SupplierNoteIcon = ({note, visible, onClick}) => {
    if (roles.includes('coordinator') || roles.includes('sales')) {
      if (note && !!note.length) {
        return <Icon onClick={() => onClick()} icon={['fas', 'comment']} className="text-primary cursor-pointer" />
      } else {
        return <Icon onClick={() => onClick()} icon={['fas', 'comment-slash']} className="text-gray-300 cursor-pointer" />
      }
    } else if (note && !!note.length && visible) {
      return <Icon onClick={() => onClick()} icon={['fas', 'comment']} className="text-primary cursor-pointer"/>
    } else {
      return <Icon icon={['fas', 'comment-slash']} className="text-gray-300 cursor-not-allowed" />
    }
  }

  const calculateAmount = (buyouts, key) => {
    let totalAmount = 0
    buyouts.forEach(buyout => {
      totalAmount += parseFloat(buyout[key]) || 0
    })

    if (!!totalAmount) {
      const currency = key == 'cost' ? buyouts[0]?.costCurrency?.iso : buyouts[0]?.priceCurrency?.iso
      return totalAmount + ' ' + currency
    }

    return ''
  }

  const formatBuyouts = (buyouts) => {

    return buyouts.map((buyout) => ({
      quantity: buyout.quantity,
      quantity_type: buyout.quantity_type.name,
      ordertargetmedia: buyout.ordertargetmedia.name,
      territoryMarket: buyout.territoryMarket.name,
      orderusage: buyout.orderusage.name,
      amount: buyout.price,
      currency: buyout.priceCurrency.iso
    }))
  }
  
  const formatFilePreview = (entry) => {

    if (!(roles.includes('coordinator') || roles.includes('sales')) && entry.files && !!entry.files.length && !['READY', 'CLOSED'].includes(entry.status)) {
      return <FormattedMessage {...messages.noPreview} />
    }

    return( 
      <div className="space-y-1">
        {entry.files && entry.files.map((file) => 
          <FilePreview {...file}/>
        )}
      </div>
    )
  }

  const showCommentModal = (taskId, supplier_note_internal, supplier_note_external) => {
    NiceModal.show(SubmittedTableCommentModel, {taskId, supplier_note_internal, supplier_note_external} )
      .then(() => refetch())
      .catch((error) => {
        toastr.error(error)
      })
  }

  const onFileDownload = (entry) => {
    if ((roles.includes('coordinator') || roles.includes('sales')) && entry.filesDownloadLink) {
      return window.open(entry.filesDownloadLink || '', '_blank')
    }

    if (['READY', 'CLOSED'].includes(entry.status) && entry.filesDownloadLink) {
      return window.open(entry.filesDownloadLink || '', '_blank')
    }
    toastr.error(formatMessage(messages.noFiles))
  }

  const DownloadAllButton = () => {
    if (entries?.scope && entries.scope.some((entry) => entry.filesDownloadLink)) {
      const readyOrClosedEntries = entries.scope.filter((entry) => ['READY', 'CLOSED'].includes(entry.status) && !!entry.files.length)
      if (!!readyOrClosedEntries.length) {
        return (
          <div className="flex justify-end m-2">
            <Button size="xs" onClick={() => window.open(`/download/order/files/${orderId}/visible`)}>
              <FormattedMessage {...messages.downloadAllFiles} /> 
            </Button>
          </div>
        )
      }
    }
    return null
  }

  const onBulkReject = () => {
    NiceModal.show(SubmittedTableRejectModel, {selectedTaskIds: selectedTaskIds, orderId: orderId} )
      .then(() => refetch())
      .catch((error) => {
        toastr.error(error)
      })
  }

  return (
    <div className='bg-white'>
      <div className="overflow-x-scroll w-full">
        <DataTable
          rowActions={(roles.includes('coordinator') || roles.includes('sales')) ? [
            {
              label: formatMessage(messages.duplicate),
              onClick: (entry) => {
                delete entry.taskId
                onDuplicate(entry)
              }
            },
            {
              label: formatMessage(messages.edit),
              onClick: (entry) => onDuplicate(entry)
            },
            {
              label: formatMessage(messages.downloadFile),
              onClick: (entry) => onFileDownload(entry),
            }
          ] : []}
          rowsSelected={selectedTaskIds}
          rowSelectionKey="taskId"
          onRowSelected={(key) => {
            if (selectedTaskIds.includes(key)) {
              setSelectedTaskIds((prev) => prev.filter((id) => id !== key))
            } else {
              setSelectedTaskIds((prev) => [...prev, key])
            }
          }}
          noDataMessage={formatMessage(messages.noData)}
          className="w-full"
          head={head()}
          body={body()}
          entries={entries ? entries.scope : []}
        />
        {!(roles.includes('coordinator') || roles.includes('sales')) &&
          <DownloadAllButton/>
        }
        {(roles.includes('coordinator') || roles.includes('sales')) && !!selectedTaskIds.length &&
          <div className="p-2">
            <Button size="xs" state="negative" onClick={() => onBulkReject()}>
              <FormattedMessage {...messages.sendRejectMessage} /> 
            </Button>
          </div>
        }
      </div>
    </div>
  )
}

export default SubmittedTable
